import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useProfesional } from "../context/ProfesionalContext";
import { safeFetch } from "@/api/safeFetch";
import Alerta from "@/components/alert/Alerta";
import { isValidEmail } from "@/components/validation/validations";

const ContactoLanding: React.FC = () => {
  const location = useLocation();
  const empleo = location.state?.empleo;
  const esSolicitudEmpleo = !!empleo;

  const { landingData } = useProfesional();

  const styles = landingData?.theme?.globalStyles;

  // ⚠️ Cambia esta línea si el id tiene otro nombre
  const correoDestino = landingData?.contact.correo_perfil;

  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [mensaje, setMensaje] = useState("");

  const [cv, setCv] = useState<File | null>(null);
  const [imagen, setImagen] = useState<File | null>(null);

  const [enviando, setEnviando] = useState(false);

  const [alerta, setAlerta] = useState<{
    mensaje: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);

  const enviarFormulario = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // ===============================
    // VALIDACIONES
    // ===============================

    if (!nombre.trim()) {
      setAlerta({
        mensaje: "El nombre es obligatorio.",
        type: "warning",
      });
      return;
    }

    if (!email.trim()) {
      setAlerta({
        mensaje: "El correo electrónico es obligatorio.",
        type: "warning",
      });
      return;
    }

    if (!isValidEmail(email)) {
      setAlerta({
        mensaje: "Introduce un correo electrónico válido.",
        type: "warning",
      });
      return;
    }

    if (!mensaje.trim()) {
      setAlerta({
        mensaje: "El mensaje es obligatorio.",
        type: "warning",
      });
      return;
    }

    // Sólo para solicitudes de empleo
    if (esSolicitudEmpleo && !cv) {
      setAlerta({
        mensaje: "Debes adjuntar tu currículum.",
        type: "warning",
      });
      return;
    }

    try {
      setEnviando(true);

      const formData = new FormData();

      formData.append("correoPerfil", correoDestino ?? "");
      formData.append("nombre", nombre);
      formData.append("email", email);
      formData.append("mensaje", mensaje);

      if (empleo) {
        formData.append("oferta", empleo.titulo);
      }

      if (cv) {
        formData.append("cv", cv);
      }

      if (imagen) {
        formData.append("imagen", imagen);
      }

      const response = await safeFetch("/api/contactoLanding/enviar-cv", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.mensaje);
      }

      setAlerta({
        mensaje: "Currículum enviado correctamente",
        type: "success",
      });

      setNombre("");
      setEmail("");
      setMensaje("");
      setCv(null);
      setImagen(null);
    } catch (error: any) {
      console.error(error);

      setAlerta({
        mensaje: error?.message || "No se pudo enviar la candidatura",
        type: "error",
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}
      <div className="w-full min-h-[calc(100vh-120px)] flex items-center justify-center py-4 lg:py-0 pt-10">
        <div className="w-full max-w-[90vw] sm:max-w-lg md:max-w-xl lg:max-w-xl mx-auto text-white pb-10">
          {esSolicitudEmpleo && empleo && (
            <div className="mb-6 rounded-2xl border border-cyan-400/30 bg-gradient-to-r from-cyan-500/15 to-blue-600/10 backdrop-blur-xl p-5 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-cyan-500/20 border border-cyan-400/30 text-2xl">
                  💼
                </div>

                <div className="flex-1">
                  <p
                    className="text-sm uppercase tracking-widest font-medium"
                    style={{
                      color: styles?.accentColor,
                      fontFamily: styles?.fontFamily,
                    }}
                  >
                    Solicitud de empleo
                  </p>

                  <h3
                    className="mt-1 font-bold"
                    style={{
                      color: styles?.h3Color,
                      fontFamily: styles?.fontFamily,
                    }}
                  >
                    {empleo.titulo}
                  </h3>

                  <p
                    className="mt-1"
                    style={{
                      color: styles?.textColor,
                      fontFamily: styles?.fontFamily,
                    }}
                  >
                    Completa el formulario y adjunta tu currículum para enviar
                    tu candidatura.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white/10 backdrop-blur-2xl border-2 border-white/20 rounded-2xl sm:rounded-3xl shadow-md shadow-zinc-800 p-5 sm:p-6 md:p-8 lg:p-10 text-center">
            <h2
              className="font-bold mb-3"
              style={{
                color: styles?.h2Color,
                fontFamily: styles?.fontFamily,
              }}
            >
              {esSolicitudEmpleo ? "Enviar candidatura" : "Contacto"}
            </h2>

            <p
              className="mb-6 sm:mb-8"
              style={{
                color: styles?.textColor,
                fontFamily: styles?.fontFamily,
              }}
            >
              {esSolicitudEmpleo
                ? "Completa el formulario y adjunta tu currículum para postularte a esta oferta."
                : "Ponte en contacto con nosotros para cualquier consulta."}
            </p>

            <form onSubmit={enviarFormulario} className="flex flex-col gap-4">
              <input
                type="text"
                required
                placeholder="Nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-cyan-400 transition"
              />

              <input
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-cyan-400 transition"
              />

              <textarea
                placeholder="Mensaje"
                required
                rows={4}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-cyan-400 resize-none transition"
              />

              {esSolicitudEmpleo && (
                <>
                  <div className="text-left">
                    <label
                      className="block mb-2 font-medium"
                      style={{
                        color: styles?.textColor,
                        fontFamily: styles?.fontFamily,
                      }}
                    >
                      Currículum (PDF, DOC o DOCX)
                    </label>

                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      required={esSolicitudEmpleo}
                      onChange={(e) => setCv(e.target.files?.[0] ?? null)}
                      className="block w-full text-sm text-white
                    file:mr-4
                    file:px-4
                    file:py-2
                    file:rounded-xl
                    file:border-0
                    file:bg-cyan-600
                    file:text-white
                    file:cursor-pointer
                    file:hover:bg-cyan-500"
                    />

                    {cv && (
                      <p className="mt-2 text-green-400 text-sm">
                        📄 {cv.name}
                      </p>
                    )}
                  </div>

                  <div className="text-left">
                    <label className="block mb-2 text-white/80 font-medium">
                      Imagen (opcional)
                    </label>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImagen(e.target.files?.[0] ?? null)}
                      className="block w-full text-sm text-white
                    file:mr-4
                    file:px-4
                    file:py-2
                    file:rounded-xl
                    file:border-0
                    file:bg-cyan-600
                    file:text-white
                    file:cursor-pointer
                    file:hover:bg-cyan-500"
                    />

                    {imagen && (
                      <p className="mt-2 text-green-400 text-sm">
                        🖼️ {imagen.name}
                      </p>
                    )}
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={enviando}
                className="mt-4 py-3 rounded-xl font-semibold hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg disabled:opacity-60"
                style={{
                  backgroundColor: styles?.buttonColor,
                  color: styles?.buttonTextColor,
                  fontFamily: styles?.fontFamily,
                }}
              >
                {enviando
                  ? "Enviando..."
                  : esSolicitudEmpleo
                    ? "Enviar candidatura"
                    : "Enviar mensaje"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactoLanding;
